import { useEffect, useRef } from 'react';
import { NavigationType, useLocation, useNavigationType } from 'react-router-dom';

import { useAppDispatch } from '@/redux/hooks';
import { updateSearchSubmittedFromPage } from '@/redux/modules/search/search.actions';
import { getAmplitudePageNameFromPath } from '@/utils/getPageNameFromPath';

/**
 * Records the page a navigation started on as the `Page Name` of the search it leads to, whatever started it:
 * the header, a related-search chip, a filter, or a plain link.
 *
 * Must run before RouteDataLoader's loadData effect, so the search reads the page the person just left.
 * A replace rewrites the URL of the same visit, and Back/Forward revisits an earlier one; neither starts a
 * search from a new page, so both keep the recorded value.
 *
 * Not `usePreviousLocation`: its provider is a parent and updates in its own effect, after this one has run.
 */
export const useRecordSearchOriginPage = () => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const navigationType = useNavigationType();
    const previousLocationRef = useRef(location);

    useEffect(() => {
        const previousLocation = previousLocationRef.current;
        previousLocationRef.current = location;

        if (previousLocation.key === location.key || navigationType !== NavigationType.Push) {
            return;
        }
        dispatch(
            updateSearchSubmittedFromPage(
                getAmplitudePageNameFromPath(previousLocation.pathname, previousLocation.search)
            )
        );
    }, [dispatch, location, navigationType]);
};
